import React, { useState } from "react";
import WeekCalendar from "./WeekCalendar";
import MonthCalendar from "./MonthCalendar";
import Agenda from "./Agenda";
import "./utleieplan.css";

export interface Booking {
  title: string;
  start: Date;
  end: Date;
  allDay?: boolean;
  deliveryMethod?: string;
  fakturert?: boolean;
}

interface Props {
  bookings: Booking[];
  openNewBooking: () => void;
  onEditBooking: (booking: Booking) => void;
  onCloseRental: (booking: Booking) => void;
}

export const getWeekDates = (date: Date): Date[] => {
  const week = [];
  const startDate = new Date(date);
  const day = startDate.getDay();
  const diff = startDate.getDate() - day + (day === 0 ? -6 : 1);
  startDate.setDate(diff);

  for (let i = 0; i < 7; i++) {
    week.push(new Date(startDate));
    startDate.setDate(startDate.getDate() + 1);
  }
  return week;
};

export const getMonthDates = (date: Date): Date[] => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1);

  const startDate = new Date(firstDay);
  const dayOfWeek = startDate.getDay();
  const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  startDate.setDate(startDate.getDate() - daysToSubtract);

  const dates = [];
  for (let i = 0; i < 42; i++) {
    dates.push(new Date(startDate));
    startDate.setDate(startDate.getDate() + 1);
  }
  return dates;
};

export const isSameDay = (date1: Date, date2: Date): boolean => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

export const getEventsForDay = (date: Date, bookings: Booking[]): Booking[] => {
  return bookings.filter((booking) => {
    const start = new Date(
      booking.start.getFullYear(),
      booking.start.getMonth(),
      booking.start.getDate()
    );
    const end = new Date(
      booking.end.getFullYear(),
      booking.end.getMonth(),
      booking.end.getDate()
    );
    const targetDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );
    return targetDate >= start && targetDate <= end;
  });
};

const Utleieplan = ({
  bookings,
  openNewBooking,
  onEditBooking,
  onCloseRental,
}: Props) => {
  const [currentView, setCurrentView] = useState<
    "month" | "week" | "day" | "agenda"
  >("month");
  const [currentDate, setCurrentDate] = useState(new Date());

  const eventColors = [
    "#1b4e8c",
    "#d32f2f",
    "#388e3c",
    "#f57c00",
    "#7b1fa2",
    "#0097a7",
    "#5d4037",
    "#616161",
    "#c2185b",
    "#689f38",
  ];

  const weekDates = getWeekDates(currentDate);
  const monthDates = getMonthDates(currentDate);

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("nb-NO", {
      weekday: "long",
      day: "2-digit",
      month: "short",
    });
  };

  const formatMonth = (date: Date) => {
    return date.toLocaleDateString("nb-NO", {
      month: "long",
      year: "numeric",
    });
  };

  const navigate = (direction: number) => {
    const newDate = new Date(currentDate);
    if (currentView === "month") {
      newDate.setMonth(currentDate.getMonth() + direction);
    } else {
      newDate.setDate(currentDate.getDate() + direction * 7);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const getDateRange = () => {
    if (currentView === "month") {
      return formatMonth(currentDate);
    } else {
      return `${formatDate(weekDates[0])} - ${formatDate(weekDates[6])}`;
    }
  };

  const expiredCount = bookings.filter(
    (b) => b.end < new Date() && !b.fakturert
  ).length;

  return (
    <div className="utleieplan-container">
      <div className="utleieplan-header">
        <h2 className="utleieplan-title">Utleieplan</h2>
        <button className="ny-utleie-button" onClick={openNewBooking}>
          Ny utleie
        </button>
      </div>

      <div className="calendar-toolbar">
        <div className="toolbar-nav">
          <button onClick={() => navigate(-1)} className="nav-button">
            Forrige
          </button>
          <button onClick={goToToday} className="nav-button">
            I dag
          </button>
          <button onClick={() => navigate(1)} className="nav-button">
            Neste
          </button>
        </div>

        <span className="toolbar-label">{getDateRange()}</span>

        <div className="toolbar-views">
          <button
            onClick={() => setCurrentView("month")}
            className={`view-button ${currentView === "month" ? "active" : ""}`}
          >
            Måned
          </button>
          <button
            onClick={() => setCurrentView("week")}
            className={`view-button ${currentView === "week" ? "active" : ""}`}
          >
            Uke
          </button>
          <button
            onClick={() => setCurrentView("day")}
            className={`view-button ${currentView === "day" ? "active" : ""}`}
          >
            Dag
          </button>
          <button
            onClick={() => setCurrentView("agenda")}
            className={`view-button ${
              currentView === "agenda" ? "active" : ""
            }`}
          >
            Utløpte leier
            {expiredCount > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-red-600 text-white text-xs rounded-full">
                {expiredCount}
              </span>
            )}
          </button>
        </div>
      </div>

      <div className="calendar-container">
        <div className="calendar-headers">
          {[
            "mandag",
            "tirsdag",
            "onsdag",
            "torsdag",
            "fredag",
            "lørdag",
            "søndag",
          ].map((dayName, index) => (
            <div key={index} className="calendar-header">
              {dayName}
            </div>
          ))}
        </div>

        {currentView === "week" ? (
          <WeekCalendar
            bookings={bookings}
            weekDates={weekDates}
            eventColors={eventColors}
          />
        ) : currentView === "month" ? (
          <MonthCalendar
            bookings={bookings}
            monthDates={monthDates}
            eventColors={eventColors}
            currentDate={currentDate}
          />
        ) : currentView === "agenda" ? (
          <Agenda
            bookings={bookings}
            onEdit={onEditBooking}
            onCloseRental={onCloseRental}
          />
        ) : (
          <div className="placeholder-view">
            <p>Dagvisning kommer snart…</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Utleieplan;
